/**
 * Shared upload functionality for Image and Video modals
 * Provides standardized upload handling with both uploadManager and uploadHandler support
 */
export const uploadMixin = {
  props: {
    uploadHandler: {
      type: Function,
      default: null
    },
    uploadManager: {
      type: Object,
      default: null
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024 // 10MB default
    },
    allowedTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isUploading: false,
      uploadProgress: 0,
      uploadError: null,
      uploadedUrl: null
    }
  },

  methods: {
    /**
     * Validate file type and size
     */
    validateFile(file, fileType = 'file') {
      // Validate file type
      const typePrefix = fileType === 'image' ? 'image/' : fileType === 'video' ? 'video/' : ''
      if (typePrefix && !file.type.startsWith(typePrefix)) {
        throw new Error(`Please select a valid ${fileType} file.`)
      }

      // Validate against allowed types if specified
      if (this.allowedTypes.length > 0 && !this.allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} is not allowed.`)
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        const maxSizeMB = Math.round(this.maxFileSize / (1024 * 1024))
        throw new Error(`File size must be less than ${maxSizeMB}MB.`)
      }

      return true
    },

    /**
     * Handle upload using uploadManager (new system)
     */
    async handleApiUpload(file) {
      this.isUploading = true
      this.uploadError = null
      this.uploadProgress = 0
      
      try {
        const result = await this.uploadManager.upload(file, {
          onProgress: (progress) => {
            this.uploadProgress = progress.percentage || 0
          }
        })
        
        // Extract URL from result - handle different response formats
        const src = result?.src || result?.url || result?.publicUrl || result?.location || result?.href
        
        if (!src && typeof result === 'string') {
          // Handle case where result is directly a URL string
          this.uploadedUrl = result
          this.isUploading = false
          return result
        }
        
        if (!src) {
          throw new Error('No URL found in upload result')
        }
        
        this.uploadedUrl = src
        this.isUploading = false
        return src
      } catch (error) {
        this.uploadError = this.getErrorMessage(error)
        this.isUploading = false
        throw error
      }
    },

    /**
     * Handle upload using uploadHandler (legacy system)
     */
    async handleLegacyUpload(file) {
      this.isUploading = true
      this.uploadError = null
      this.uploadProgress = 0
      
      try {
        const result = await this.uploadHandler(file)
        
        // Extract URL from result - handle different response formats
        const src = result?.src || result?.url || result?.publicUrl || result?.location || result?.href
        
        if (!src && typeof result === 'string') {
          // Handle case where result is directly a URL string
          this.uploadedUrl = result
          this.isUploading = false
          return result
        }
        
        if (!src) {
          throw new Error('No URL found in upload result')
        }
        
        this.uploadedUrl = src
        this.isUploading = false
        return src
      } catch (error) {
        this.uploadError = this.getErrorMessage(error)
        this.isUploading = false
        throw error
      }
    },

    /**
     * Auto-upload file using available upload method
     */
    async autoUpload(file) {
      if (this.uploadManager) {
        return await this.handleApiUpload(file)
      } else if (this.uploadHandler) {
        return await this.handleLegacyUpload(file)
      }
      return null
    },

    /**
     * Retry upload
     */
    async retryUpload(file) {
      if (this.uploadManager) {
        return await this.handleApiUpload(file)
      } else if (this.uploadHandler) {
        return await this.handleLegacyUpload(file)
      }
      throw new Error('No upload method available')
    },

    /**
     * Get user-friendly error message
     */
    getErrorMessage(error) {
      if (error.message?.includes('network') || error.message?.includes('timeout')) {
        return 'Network error. Please check your connection and try again.'
      } else if (error.message?.includes('size')) {
        return 'File too large. Please choose a smaller file.'
      } else if (error.message?.includes('format') || error.message?.includes('type')) {
        return 'Invalid file format. Please choose a valid file.'
      }
      return error.message || 'Upload failed. Please try again.'
    },

    /**
     * Format file size for display
     */
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    /**
     * Reset upload state
     */
    resetUploadState() {
      this.isUploading = false
      this.uploadProgress = 0
      this.uploadError = null
      this.uploadedUrl = null
    }
  }
}